import {
  type ConnectedAccountResponseDto,
  type CreateConnectedAccountDto,
  createConnectedAccountMutation,
  deleteConnectedAccountMutation,
  getConnectedAccountsOptions,
  getConnectedAccountsQueryKey,
  type GetConnectedAccountsData,
} from '@helios/frontend-api-v2-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { getFriendlyError } from '../../utils/error';

export const QUERY_KEY_CONNECTED_ACCOUNTS = getConnectedAccountsQueryKey;

interface UseConnectedAccountsProps {
  data: ConnectedAccountResponseDto[];
  error: string | null;
  isLoading: boolean;
  isFetched: boolean;
  errorStatusCode?: number;
}

interface UseConnectedAccountsParams {
  params?: GetConnectedAccountsData['query'];
  refetchInterval?: number;
}

export const useConnectedAccounts = ({
  params,
  refetchInterval,
}: UseConnectedAccountsParams): UseConnectedAccountsProps => {
  const { data, error, isPending, isFetched } = useQuery({
    ...getConnectedAccountsOptions({ query: params }),
    refetchInterval,
  });

  return {
    data: data ?? [],
    error: getFriendlyError(error as AxiosError, 'connected accounts') || null,
    isLoading: isPending,
    isFetched,
    errorStatusCode: (error as AxiosError)?.response?.status,
  };
};

interface UseCreateConnectedAccountProps {
  mutate: (request: CreateConnectedAccountDto) => void;
  isLoading: boolean;
  error: string | null;
  data: ConnectedAccountResponseDto | undefined;
  isSuccess: boolean;
  reset: () => void;
}

export const useCreateConnectedAccount = (): UseCreateConnectedAccountProps => {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, data, isSuccess, reset } = useMutation({
    ...createConnectedAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_CONNECTED_ACCOUNTS() });
    },
  });

  return {
    mutate: (request: CreateConnectedAccountDto) => mutate({ body: request }),
    isLoading: isPending,
    error: getFriendlyError(error as AxiosError, 'connected accounts') || null,
    data,
    isSuccess,
    reset,
  };
};

interface UseDeleteConnectedAccountProps {
  mutate: (id: string) => void;
  isLoading: boolean;
  error: string | null;
  isSuccess: boolean;
  reset: () => void;
}

export const useDeleteConnectedAccount = (): UseDeleteConnectedAccountProps => {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, isSuccess, reset } = useMutation({
    ...deleteConnectedAccountMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY_CONNECTED_ACCOUNTS() });
    },
  });

  return {
    mutate: (id: string) => mutate({ path: { id } }),
    isLoading: isPending,
    error: getFriendlyError(error as AxiosError, 'connected accounts') || null,
    isSuccess,
    reset,
  };
};
